import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import { Icon } from '@iconify/react';
import { LoadingButton } from '@mui/lab';
import { Alert, IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import * as jose from 'jose';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { ChildNewEditFormValues } from 'components/children/ChildNewEditForm';
import { logout, registerChild, registerParent, registerStaff } from 'external/firebase/controllers/auth';
import useLocales from 'hooks/useLocales';
import { PATH_AUTH } from 'routes/paths';

export interface RegisterUserFormValues {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
}

interface UserPayload extends ChildNewEditFormValues {
    parent: string;
}

const RegisterForm: React.FunctionComponent = () => {
    const navigate = useNavigate();
    const { search } = useLocation();
    const searchParams = new URLSearchParams(search);
    const { translate } = useLocales();
    const [showPassword, setShowPassword] = useState(false);
    const [isChildrenRegistrationUsingLink, setIsChildrenRegistrationUsingLink] = useState(false);
    const [children, setChildren] = useState<UserPayload | undefined>();

    const code = searchParams.get('code') || '';

    const RegisterSchema = Yup.object().shape({
        firstName: Yup.string()
            .min(2, translate('too_short'))
            .max(50, translate('too_long'))
            .required(translate('first_name_required')),
        lastName: Yup.string()
            .min(2, translate('too_short'))
            .max(50, translate('too_long'))
            .required(translate('last_name_required')),
        email: Yup.string()
            .email(translate('email_address_must_be_valid'))
            .required(translate('email_address_required')),
        password: Yup.string()
            .min(8, translate('password_must_be_at_least_8_characters'))
            .required(translate('password_required')),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            firstName: children?.firstName || '',
            lastName: children?.lastName || '',
            email: children?.email || '',
            password: '',
        },
        validationSchema: RegisterSchema,
        onSubmit: async (
            values: RegisterUserFormValues,
            { setSubmitting, setStatus, resetForm }: FormikHelpers<RegisterUserFormValues>,
        ) => {
            // logout user if logged in
            await logout();
            // eslint-disable-next-line no-nested-ternary
            const res = values.email.match(
                /@codeacademy.lt|@codeacademykids.lt|@codeacademykids.com|@codeacademylt.onmicrosoft.com/,
            )
                ? await registerStaff(values.email, values.password, values.firstName, values.lastName)
                : isChildrenRegistrationUsingLink
                ? await registerChild(
                      values.email,
                      values.password,
                      values.firstName,
                      values.lastName,
                      children?.parent || '',
                  )
                : await registerParent(values.email, values.password, values.firstName, values.lastName);
            setSubmitting(false);
            if (!res.ok) {
                resetForm();
                setStatus(translate(res.code));
                return;
            }
            navigate(PATH_AUTH.verify, { replace: true });
        },
    });

    const { errors, status, setStatus, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

    const decryptJwt = async (code: string) => {
        const secret = jose.base64url.decode('zH4NRP1HMALxxCFnRZABFA7GOJtzU_gIj02alfL1lvI');
        try {
            const jwtToken = await jose.jwtDecrypt(code, secret);
            setChildren({
                firstName: jwtToken.payload.firstName,
                lastName: jwtToken.payload.lastName,
                email: jwtToken.payload.email,
                parent: jwtToken.payload.parent,
            } as UserPayload);
        } catch (e) {
            setStatus(translate('incorrect_or_tampered_link'));
        }
    };

    useEffect(() => {
        if (code) {
            decryptJwt(code);
            setIsChildrenRegistrationUsingLink(true);
        }
    }, [code]);

    return (
        <FormikProvider value={formik}>
            <Form autoComplete='off' noValidate onSubmit={handleSubmit}>
                <Stack spacing={3}>
                    {status && <Alert severity='error'>{status}</Alert>}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            fullWidth
                            disabled={isChildrenRegistrationUsingLink}
                            label={translate('first_name')}
                            {...getFieldProps('firstName')}
                            error={Boolean(touched.firstName && errors.firstName)}
                            helperText={touched.firstName && errors.firstName}
                        />

                        <TextField
                            fullWidth
                            disabled={isChildrenRegistrationUsingLink}
                            label={translate('last_name')}
                            {...getFieldProps('lastName')}
                            error={Boolean(touched.lastName && errors.lastName)}
                            helperText={touched.lastName && errors.lastName}
                        />
                    </Stack>

                    <TextField
                        fullWidth
                        disabled={isChildrenRegistrationUsingLink}
                        autoComplete='username'
                        type='email'
                        label={translate('email_address')}
                        {...getFieldProps('email')}
                        error={Boolean(touched.email && errors.email)}
                        helperText={touched.email && errors.email}
                    />

                    <TextField
                        fullWidth
                        autoComplete='current-password'
                        type={showPassword ? 'text' : 'password'}
                        label={translate('password')}
                        {...getFieldProps('password')}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <IconButton edge='end' onClick={() => setShowPassword(prev => !prev)}>
                                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        error={Boolean(touched.password && errors.password)}
                        helperText={touched.password && errors.password}
                    />

                    <LoadingButton fullWidth size='large' type='submit' variant='contained' loading={isSubmitting}>
                        {translate('register')}
                    </LoadingButton>
                </Stack>
            </Form>
        </FormikProvider>
    );
};

export default RegisterForm;
